import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { User, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { SEOHead, breadcrumbSchema } from '../components/seo/SEOHead';
import { downloadQRCodePNG, downloadQRCodeSVG } from '../utils/qrHelpers';
import { trackEvent } from '../components/analytics/GoogleAnalytics';

export const VCardQRPage: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organization, setOrganization] = useState('');
  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [address, setAddress] = useState('');

  const qrContent = `BEGIN:VCARD
VERSION:3.0
N:${lastName};${firstName}
FN:${firstName} ${lastName}
${organization ? `ORG:${organization}` : ''}
${title ? `TITLE:${title}` : ''}
${phone ? `TEL:${phone}` : ''}
${email ? `EMAIL:${email}` : ''}
${website ? `URL:${website}` : ''}
${address ? `ADR:${address}` : ''}
END:VCARD`;

  const isValid = firstName.length > 0 && lastName.length > 0;

  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', url: 'https://freeqrcodenow.com/' },
    { name: 'vCard QR Code Generator', url: 'https://freeqrcodenow.com/vcard-qr-code-generator' },
  ]);

  return (
    <>
      <SEOHead
        title="Free vCard QR Code Generator | Digital Business Card QR Codes"
        description="Create free vCard QR codes for instant contact sharing. Generate digital business card QR codes with phone, email, website, and more. Download as PNG or SVG."
        keywords="vcard qr code, business card qr, contact qr code, digital business card, vcard generator"
        structuredData={breadcrumbs}
      />

      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            <User className="inline h-10 w-10 mr-3 text-blue-600" />
            Free vCard QR Code Generator
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Create digital business card QR codes. Share your contact information instantly!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input label="First Name *" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                <Input label="Last Name *" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
              <Input label="Organization" value={organization} onChange={(e) => setOrganization(e.target.value)} />
              <Input label="Job Title" value={title} onChange={(e) => setTitle(e.target.value)} />
              <Input label="Phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <Input label="Website" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} />
              <Input label="Address" value={address} onChange={(e) => setAddress(e.target.value)} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>QR Code Preview</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center space-y-6">
              {isValid ? (
                <>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <QRCodeSVG value={qrContent} size={300} level="M" includeMargin={true} />
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={() => {
                      downloadQRCodePNG(qrContent, { size: 300, foregroundColor: '#000000', backgroundColor: '#ffffff', errorCorrectionLevel: 'M', marginSize: 4 }, false, 'vcard-qr.png');
                      trackEvent('download', 'vCard QR', 'PNG');
                    }}>
                      <Download className="h-4 w-4 mr-2" />
                      Download PNG
                    </Button>
                    <Button variant="outline" onClick={() => {
                      downloadQRCodeSVG(qrContent, { size: 300, foregroundColor: '#000000', backgroundColor: '#ffffff', errorCorrectionLevel: 'M', marginSize: 4 }, 'vcard-qr.svg');
                      trackEvent('download', 'vCard QR', 'SVG');
                    }}>
                      <Download className="h-4 w-4 mr-2" />
                      Download SVG
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <User className="h-16 w-16 mb-4 text-gray-300" />
                  <p>Enter contact details to generate QR code</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="mt-12 prose prose-lg max-w-none">
          <h2>Create Digital Business Card QR Codes</h2>
          <p>
            vCard QR codes are the modern way to share contact information. No more fumbling with business cards
            or manually entering details. Just scan and save!
          </p>

          <h2>What Information Can You Include?</h2>
          <ul>
            <li>Full name and job title</li>
            <li>Company or organization</li>
            <li>Phone number (mobile and office)</li>
            <li>Email address</li>
            <li>Website URL</li>
            <li>Physical address</li>
          </ul>

          <h2>Perfect For:</h2>
          <ul>
            <li>Networking events and conferences</li>
            <li>Business cards and brochures</li>
            <li>Email signatures</li>
            <li>Social media profiles</li>
            <li>Event badges and name tags</li>
          </ul>
        </div>
      </div>
    </>
  );
};


